import { ToastDuration } from './toast-duration.enum';
import { ToastPosition } from './toast-position.enum';
import { ToastProps } from './toast-prop.interface';
import React from 'react';
import { Toaster } from 'react-hot-toast';
import { useBreakpoint } from 'src/hooks/use-breakpoint';

/**
 * Modern Toast Component
 *
 * A beautifully designed toast notification component with glassmorphism effects, soft gradients, and smooth animations.
 * Features modern design patterns with pastel colors and clean typography to match the overall website aesthetic.
 *
 * This component provides customizable toast notifications with options to control
 * the order, position, and duration of toasts. By default:
 * - `reverseOrder` is set to false, meaning new toasts appear at the bottom.
 * - `position` is set to 'bottom-center', meaning toasts will appear at the bottom center of the screen.
 * - `duration` is set to 'Medium' (5000ms).
 *
 * @param {boolean} [reverseOrder=false] - If true, new toasts appear at the top.
 * @param {ToastPosition} [position=ToastPosition.BottomCenter] - Position where the toast appears on the screen.
 * @param {ToastDuration} [duration=ToastDuration.Medium] - Duration for how long the toast appears on the screen.
 *
 * @returns {JSX.Element} The Toaster component with customized modern styles and behavior.
 */
const Toast: React.FC<ToastProps> = ({
  reverseOrder = false,
  position = ToastPosition.BottomCenter,
  duration = ToastDuration.Medium,
}) => {
  const { isMobile } = useBreakpoint();

  return (
    <Toaster
      reverseOrder={reverseOrder}
      position={position}
      toastOptions={{
        // Modern glassmorphism styling
        className: `
          !bg-white/80 
          !backdrop-blur-md 
          !border 
          !border-gray-200/50 
          !shadow-lg 
          !shadow-gray-200/50 
          !rounded-2xl 
          !p-4 
          !font-spaceGrotesk 
          !text-gray-800 
          !text-sm 
          !font-medium 
          !leading-relaxed
          !transition-all 
          !duration-300 
          !ease-out
          !transform
          !scale-100
          hover:!scale-105
          hover:!-translate-y-1
          hover:!shadow-xl
          hover:!shadow-gray-300/50
          hover:!bg-white/90
          hover:!border-gray-300/60
          !relative
          !overflow-hidden
        `,
        duration: duration,
        style: {
          padding: '16px',
          width: isMobile ? '100%' : 'auto',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '400px',
          minWidth: '280px',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(229, 231, 235, 0.5)',
          borderRadius: '16px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          fontSize: '14px',
          fontWeight: '500',
          lineHeight: '1.6',
          fontFamily: 'DMSans, system-ui, sans-serif',
          color: '#1f2937',
        },
        // Custom styling for different toast types
        success: {
          style: {
            background: 'rgba(236, 253, 245, 0.9)',
            borderColor: 'rgba(167, 243, 208, 0.5)',
            color: '#065f46',
          },
          iconTheme: {
            primary: '#10b981',
            secondary: '#ecfdf5',
          },
        },
        error: {
          style: {
            background: 'rgba(254, 242, 242, 0.9)',
            borderColor: 'rgba(252, 165, 165, 0.5)',
            color: '#7f1d1d',
          },
          iconTheme: {
            primary: '#ef4444',
            secondary: '#fef2f2',
          },
        },
        loading: {
          style: {
            background: 'rgba(239, 246, 255, 0.9)',
            borderColor: 'rgba(147, 197, 253, 0.5)',
            color: '#1e40af',
          },
          iconTheme: {
            primary: '#3b82f6',
            secondary: '#eff6ff',
          },
        },
      }}
      containerStyle={{
        inset: isMobile ? '16px' : '24px',
        zIndex: isMobile ? 120 : 50,
      }}
    />
  );
};

export default Toast;
